import { v4 as uuid } from 'uuid';

import { type CohortBuilderModelLegacy } from '@verantos-dev/types-cohorts';
import { parseCohortDefinitionQuery } from '@verantos-dev/util-cohort-builder-typescript-runtime';

import { generateBaseLinePeriod } from './adapters/generateBaseLinePeriod';
import { generateDerivedEventFromDSL } from './adapters/generateDerivedEventFromDSL';
import { generateFollowUp } from './adapters/generateFollowUp';
import { generateIndexDateFromDSL } from './adapters/generateIndexDateFromDSL';
import { generateSimpleEventFromDSL } from './adapters/generateSimpleEventFromDSL';
import { generateStudyPeriod } from './adapters/generateStudyPeriod';
import { generateSubjectVariablesFromDSL } from './adapters/generateSubjectVariablesFromDSL';

export const generateUIModelFromDSL = (
  dsl: string | undefined,
  dirty?: boolean // set to true to annotate input fields as dirty
): CohortBuilderModelLegacy => {
  if (!dsl) {
    return {
      simpleEvents: [],
      derivedEvents: [],
      indexDate: undefined,
      subjectVariables: [],
      studyPeriodRestrictions: {
        overallStudyPeriodRestriction: undefined,
        baselinePeriodRestriction: undefined,
        followUpPeriodRestriction: undefined,
      },
    };
  }

  // HACKY STUFF UNTIL NODE LEVEL BUT IT WORKS - LET IT HAPPEN :)
  // This is needed because the parser breaks if we dont have a full cohort definition (include, 1subject, 1simple) defined.
  // Correct replacement
  let cleaned_dsl = dsl.replace(/\\"/g, '"').replace(/\\n/g, '');
  let has_additions_to_be_removed = false;
  const unique_tag_for_addition_to_cleanup = uuid();
  const subject_variable_name_to_remove = `SUBJECTS_VARIABLE::"${unique_tag_for_addition_to_cleanup}"`;
  if (!cleaned_dsl.includes('SUBJECTS_VARIABLE')) {
    const baselineIndex = cleaned_dsl.indexOf('BASELINE');
    const followUpIndex = cleaned_dsl.indexOf('FOLLOW_UP');
    const studyPeriodIndex = cleaned_dsl.indexOf('STUDY_PERIOD');

    const positions = [baselineIndex, followUpIndex, studyPeriodIndex].filter(
      (index) => index !== -1
    );
    const insertPosition = positions.length > 0 ? Math.max(...positions) : -1;

    if (insertPosition !== -1) {
      const endOfStatement = cleaned_dsl.indexOf(';', insertPosition) + 1;
      cleaned_dsl =
        cleaned_dsl.slice(0, endOfStatement) +
        ` ${subject_variable_name_to_remove} = PERSON (  ) ; ` +
        cleaned_dsl.slice(endOfStatement);
    } else {
      cleaned_dsl = `${subject_variable_name_to_remove} = PERSON (  ) ; ` + cleaned_dsl;
    }
    has_additions_to_be_removed = true;
  }
  if (!cleaned_dsl.includes('INCLUDE')) {
    cleaned_dsl = 'INCLUDE(); ' + cleaned_dsl;
  }
  // END HACKY STUFF TO CLEANUP WITH NODE LEVEL WORK

  const { cohortDefinitionTree } = parseCohortDefinitionQuery(cleaned_dsl);

  const isSubjectVariablePresent = cohortDefinitionTree.subjects_variable();
  //  && customErrorListener.getErrors().length === 0;
  const isSimpleEventPresent = cohortDefinitionTree.simple_event();
  const isDerivedEventPresent = cohortDefinitionTree.derived_event();

  const isIndexDatePresent = !!(
    cohortDefinitionTree.index_date && cohortDefinitionTree.index_date()
  );
  const isStudyPeriodRestrictionPresent = !!(
    cohortDefinitionTree.study_period_criteria && cohortDefinitionTree.study_period_criteria()
  );
  const isBaselinePeriodRestrictionPresent = !!(
    cohortDefinitionTree.baseline_period_criteria && cohortDefinitionTree.baseline_period_criteria()
  );

  const isFollowUpPeriodRestrictionPresent = !!(
    cohortDefinitionTree.follow_up_period_criteria &&
    cohortDefinitionTree.follow_up_period_criteria()
  );
  // Obtain the simple events from the DSL
  const simpleEvents =
    (isSimpleEventPresent &&
      generateSimpleEventFromDSL(cohortDefinitionTree.simple_event(), dirty)) ||
    [];

  // Obtain the derived events from the DSL
  const derivedEvents =
    (isDerivedEventPresent &&
      generateDerivedEventFromDSL(cohortDefinitionTree.derived_event(), dirty)) ||
    [];

  const indexDate = isIndexDatePresent ? generateIndexDateFromDSL(cohortDefinitionTree) : undefined;

  // Obtain the subject variables from the DSL

  const subjectVariables =
    (isSubjectVariablePresent && generateSubjectVariablesFromDSL(cohortDefinitionTree, dirty)) ||
    [];

  // obtain the study period restrictions from the DSL

  const overallStudyPeriodRestriction = isStudyPeriodRestrictionPresent
    ? generateStudyPeriod(cohortDefinitionTree)
    : undefined;
  const baselinePeriodRestriction = isBaselinePeriodRestrictionPresent
    ? generateBaseLinePeriod(cohortDefinitionTree)
    : undefined;

  const followUpPeriodRestriction = isFollowUpPeriodRestrictionPresent
    ? generateFollowUp(cohortDefinitionTree)
    : undefined;

  return {
    simpleEvents: simpleEvents || [],
    derivedEvents: derivedEvents || [],
    indexDate: indexDate,
    subjectVariables:
      subjectVariables.filter((subjectVariable) => {
        if (!has_additions_to_be_removed) return true;
        return subjectVariable.name !== unique_tag_for_addition_to_cleanup;
      }) || [],
    studyPeriodRestrictions: {
      overallStudyPeriodRestriction,
      baselinePeriodRestriction,
      followUpPeriodRestriction,
    },
  };
};
